import { create } from 'zustand';
import { TEX_RESOLUTIONS, type TexRes } from '../paintingConstants';

// VFX convention: displayed/output frame numbers start at 1001, not 0.
// Internal store frame indices remain 0-based; add this offset only for display and file output.
export const FRAME_OFFSET = 1001;

// ── Preferences persistence ───────────────────────────────────────────────────
const PREFS_KEY = 'sb-prefs';

interface SavedPrefs {
  viewportBgMode?: 'black' | 'white' | 'transparent';
  uvBgMode?: 'black' | 'white' | 'transparent';
  showGrid?: boolean;
  showManipulator?: boolean;
  heroFeaturesEnabled?: boolean;
  dupMode?: 'hero' | 'diptych' | 'triptych' | 'quadriptych';
  toolMode?: 'draw' | 'fill' | 'erase' | 'stamp' | 'text' | 'blur';
  brushSize?: number;
  brushOpacity?: number;
  blurRadius?: number;
  brushColor?: string;
  usePressure?: boolean;
  simPressure?: boolean;
  simPressureRandomness?: number;
  drawProjectionMode?: 'wrap' | 'project' | 'inverse';
  stampProjectionMode?: 'wrap' | 'project' | 'inverse';
  stampTint?: boolean;
  smoothness?: number;
  texResLabel?: string;
  autoKey?: 0 | 1 | 2;
  customColors?: string[];
  showFps?: boolean;
  showHotkeyHud?: boolean;
  fillFloodMode?: 'island' | 'noncontiguous' | 'global';
  fillTolerance?: number;
  overlayArchitecture?: boolean;
  overlaySafety?: boolean;
  tabletMode?: boolean;
  cameraFov?: number;
  lastSeenVersion?: string;
}

const _savedPrefs: SavedPrefs = (() => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}'); }
  catch { return {}; }
})();

// Migrate legacy custom-colors key on first load
if (!_savedPrefs.customColors) {
  try {
    const legacy = localStorage.getItem('sb-custom-colors');
    if (legacy) _savedPrefs.customColors = JSON.parse(legacy);
  } catch {}
}

// ── Keyframe types ────────────────────────────────────────────────────────────

export type KeyframeProp = 'opacity' | 'rotateX' | 'rotateY' | 'rotateZ' | 'translateX' | 'translateY';
export const ALL_PROPS: KeyframeProp[] = ['opacity', 'rotateX', 'rotateY', 'rotateZ', 'translateX', 'translateY'];

export interface LayerKeyframes {
  opacity:    Record<number, number>;
  rotateX:    Record<number, number>;
  rotateY:    Record<number, number>;
  rotateZ:    Record<number, number>;
  translateX: Record<number, number>;
  translateY: Record<number, number>;
}

export function emptyLayerKeyframes(): LayerKeyframes {
  return { opacity: {}, rotateX: {}, rotateY: {}, rotateZ: {}, translateX: {}, translateY: {} };
}

// ── Camera keyframe type ──────────────────────────────────────────────────────
export type CameraKeyframe = Record<number, { x: number; y: number; z: number; fov: number }>;

// ── Keyframe easing types ─────────────────────────────────────────────────────
export type EasingType = 'stepped' | 'linear' | 'bezier';
export interface KeyframeEasingData { in: EasingType; out: EasingType; }
/** layerId → prop → frame → { in, out } */
export type LayerEasings = Partial<Record<KeyframeProp, Record<number, KeyframeEasingData>>>;

// ── Render settings ───────────────────────────────────────────────────────────

export interface RenderSettings {
  outputName: string;
  bgMode: 'black' | 'white' | 'transparent';
  startFrame: number;
  endFrame: number;
  stepFrames: number;
  outputFormat: 'png-sequence' | 'webm' | 'mp4';
}

export const DEFAULT_RENDER_SETTINGS: RenderSettings = {
  outputName: 'Untitled',
  bgMode: 'white',
  startFrame: 0,
  endFrame: 899,
  stepFrames: 1,
  outputFormat: 'png-sequence',
};

// ── Sorted-frames cache ──────────────────────────────────────────────────────
// Zustand creates new prop-record objects on every setKeyframe, so the WeakMap
// entry for the old object is automatically collected — no manual invalidation.
const _sortedFramesCache = new WeakMap<object, number[]>();
function getSortedFrames(kfs: Record<number, unknown>): number[] {
  let cached = _sortedFramesCache.get(kfs);
  if (!cached) {
    cached = Object.keys(kfs).map(Number).sort((a, b) => a - b);
    _sortedFramesCache.set(kfs, cached);
  }
  return cached;
}

/** Interpolation between adjacent numeric keyframes with optional per-keyframe easing. */
export function interpolateNum(
  kfs: Record<number, number>,
  frame: number,
  easings?: Record<number, KeyframeEasingData>,
): number | null {
  const frames = getSortedFrames(kfs);
  if (!frames.length) return null;
  if (frame <= frames[0]) return kfs[frames[0]];
  if (frame >= frames[frames.length - 1]) return kfs[frames[frames.length - 1]];
  // Binary search for the bracket [lo, hi] around `frame`
  let left = 0, right = frames.length - 1;
  while (left < right - 1) {
    const mid = (left + right) >> 1;
    if (frames[mid] <= frame) left = mid; else right = mid;
  }
  const lo = frames[left];
  const hi = frames[right];
  const loOut = easings?.[lo]?.out ?? 'linear';
  const hiIn  = easings?.[hi]?.in  ?? 'linear';
  // Stepped: hold the lo value until the hi keyframe
  if (loOut === 'stepped' || hiIn === 'stepped') return kfs[lo];
  let t = (frame - lo) / (hi - lo);
  // Bezier (smoothstep) easing
  if (loOut === 'bezier' && hiIn === 'bezier') {
    t = t * t * (3 - 2 * t);         // ease-in-out (smoothstep)
  } else if (loOut === 'bezier') {
    t = t * t;                        // ease-out from lo (slow start)
  } else if (hiIn === 'bezier') {
    t = 1 - (1 - t) * (1 - t);       // ease-in to hi (slow end)
  }
  return kfs[lo] + (kfs[hi] - kfs[lo]) * t;
}

/** Step interpolation for booleans (holds the last set value). */
export function interpolateBool(kfs: Record<number, boolean>, frame: number): boolean | null {
  const frames = getSortedFrames(kfs);
  if (!frames.length) return null;
  if (frame <= frames[0]) return kfs[frames[0]];
  if (frame >= frames[frames.length - 1]) return kfs[frames[frames.length - 1]];
  // Binary search for the last frame <= `frame`
  let left = 0, right = frames.length - 1;
  while (left < right) {
    const mid = (left + right + 1) >> 1;
    if (frames[mid] <= frame) left = mid; else right = mid - 1;
  }
  return kfs[frames[left]];
}

/**
 * Applies keyframe interpolation to a layer array at a given frame.
 * Returns a new array; unchanged layers are returned by identity (no mutation).
 */
export function interpolateLayers(
  layers: Layer[],
  frame: number,
  allKeyframes: Record<string, LayerKeyframes>,
  allEasings: Record<string, LayerEasings>,
): Layer[] {
  const f = Math.round(frame);
  return layers.map((layer) => {
    const kfs = allKeyframes[layer.id];
    if (!kfs) return layer;
    const hasAny = ALL_PROPS.some((p) => Object.keys(kfs[p]).length > 0);
    if (!hasAny) return layer;
    const eas = allEasings[layer.id];
    const patch: Partial<Layer> = {};
    const op = interpolateNum(kfs.opacity, f, eas?.opacity);
    if (op !== null) patch.opacity = op;
    const rx = interpolateNum(kfs.rotateX,    f, eas?.rotateX);
    const ry = interpolateNum(kfs.rotateY,    f, eas?.rotateY);
    const rz = interpolateNum(kfs.rotateZ,    f, eas?.rotateZ);
    if (rx !== null || ry !== null || rz !== null) {
      patch.rotate = { x: rx ?? layer.rotate.x, y: ry ?? layer.rotate.y, z: rz ?? layer.rotate.z };
    }
    const tx = interpolateNum(kfs.translateX, f, eas?.translateX);
    const ty = interpolateNum(kfs.translateY, f, eas?.translateY);
    if (tx !== null || ty !== null) {
      patch.translate = { x: tx ?? layer.translate.x, y: ty ?? layer.translate.y };
    }
    return Object.keys(patch).length ? { ...layer, ...patch } : layer;
  });
}

// ── Layer type ────────────────────────────────────────────────────────────────

export interface Layer {
  id: string;
  name: string;
  visible: boolean;
  locked?: boolean;
  opacity: number;
  translate: { x: number; y: number };
  rotate: { x: number; y: number; z: number }; // degrees per axis
  /** First frame (inclusive) the layer is visible during playback. Defaults to 0. */
  frameStart?: number;
  /** Last frame (inclusive) the layer is visible during playback. Defaults to totalFrames-1. */
  frameEnd?: number;
  /** Parent layer id. When set, this layer inherits visibility, lock, rotation, and translation offsets from the parent. */
  parentId?: string;
  /** The dupMode that was active the last time pixels were painted/stamped/filled on this layer. */
  lastDupMode?: 'hero' | 'diptych' | 'triptych' | 'quadriptych';
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface AppState {
  layers: Layer[];
  selectedLayerId: string | null;
  selectLayer: (id: string | null) => void;
  // Multi-select for timeline: stored as array but used as a set
  selectedLayerIds: string[];
  addToSelection: (id: string) => void;
  removeFromSelection: (id: string) => void;
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  selectLayers: (ids: string[]) => void;
  selectLayerRange: (fromId: string, toId: string) => void;
  addLayer: (layer: Layer) => void;
  updateLayer: (id: string, patch: Partial<Omit<Layer, 'id'>>) => void;
  removeLayer: (id: string) => void;
  moveLayer: (fromIndex: number, toIndex: number) => void;
  setLayersOrder: (layers: Layer[]) => void;
  // layer merge
  pendingMergeDown: { topId: string; bottomId: string } | null;
  requestMergeLayerDown: (topId: string, bottomId: string) => void;
  clearMergeRequest: () => void;
  // drawing texture support
  textureCanvas: HTMLCanvasElement | null;
  setTextureCanvas: (c: HTMLCanvasElement) => void;
  textureVersion: number;
  bumpTextureVersion: () => void;
  // per-layer preview thumbnails (data URLs)
  layerThumbnails: Record<string, string>;
  setLayerThumbnail: (id: string, dataUrl: string) => void;
  // UV view canvases (populated by Viewport3D after each stroke)
  uvFullCanvas: HTMLCanvasElement | null;
  setUvFullCanvas: (c: HTMLCanvasElement | null) => void;
  /** Incremented whenever a UV canvas has been repainted — forces UVPanel to re-draw
   *  even though the canvas object reference doesn't change. */
  uvCanvasVersion: number;
  bumpUvCanvas: () => void;
  // stamp tool
  stampImage: HTMLCanvasElement | null;
  setStampImage: (c: HTMLCanvasElement | null) => void;
  stampIsPreset: boolean;
  setStampIsPreset: (v: boolean) => void;
  stampFlipX: boolean;
  setStampFlipX: (v: boolean) => void;
  stampFlipY: boolean;
  setStampFlipY: (v: boolean) => void;
  inverseWarpK: number;
  setInverseWarpK: (v: number) => void;
  inverseWarpStrength: number;
  setInverseWarpStrength: (v: number) => void;
  // clipboard paste
  clipboardPasteCanvas: HTMLCanvasElement | null;
  clipboardPasteScale: number;    // 1.0 = fit-to-layer
  clipboardPasteFitScale: number; // pixel scale factor when clipboardPasteScale===1
  setClipboardPasteCanvas: (c: HTMLCanvasElement | null) => void;
  setClipboardPasteScale: (s: number) => void;
  setClipboardPasteFitScale: (s: number) => void;
  // timeline playback
  fps: number;
  totalFrames: number;
  currentFrame: number;
  isPlaying: boolean;
  setFps: (fps: number) => void;
  setTotalFrames: (n: number) => void;
  setCurrentFrame: (n: number) => void;
  setIsPlaying: (v: boolean) => void;
  // in/out range + playback mode
  inPoint: number | null;
  outPoint: number | null;
  setInPoint: (n: number | null) => void;
  setOutPoint: (n: number | null) => void;
  playbackMode: 'once' | 'loop' | 'pingpong';
  setPlaybackMode: (m: 'once' | 'loop' | 'pingpong') => void;
  // keyframes
  layerKeyframes: Record<string, LayerKeyframes>;
  setKeyframe: (layerId: string, prop: KeyframeProp, frame: number, value: boolean | number) => void;
  removeKeyframe: (layerId: string, prop: KeyframeProp, frame: number) => void;
  // per-keyframe easing curves
  keyframeEasings: Record<string, LayerEasings>;
  setKeyframeEasing: (layerId: string, prop: KeyframeProp, frame: number, data: Partial<KeyframeEasingData>) => void;
  // auto-key mode: 0=off, 1=key existing channels only, 2=key all changed channels
  autoKey: 0 | 1 | 2;
  setAutoKey: (v: 0 | 1 | 2) => void;
  // render settings
  renderSettings: RenderSettings;
  setRenderSettings: (s: Partial<RenderSettings>) => void;
  // project management
  resetToDefaults: () => void;
  restoreProject: (data: {
    layers: Layer[];
    selectedLayerId: string | null;
    layerKeyframes: Record<string, LayerKeyframes>;
    fps: number;
    totalFrames: number;
    renderSettings?: RenderSettings;
  }) => void;
  /** Lightweight atomic restore used by layer undo/redo — does not touch fps/totalFrames/renderSettings. */
  restoreLayerState: (layers: Layer[], selectedLayerId: string | null, layerKeyframes: Record<string, LayerKeyframes>, cameraKeyframes?: CameraKeyframe) => void;
  // ── Toolbar / painting settings ─────────────────────────────────────────────
  toolMode: 'draw' | 'fill' | 'erase' | 'stamp' | 'text' | 'blur';
  setToolMode: (m: 'draw' | 'fill' | 'erase' | 'stamp' | 'text' | 'blur') => void;
  brushSize: number;
  setBrushSize: (v: number) => void;
  brushColor: string;
  setBrushColor: (v: string) => void;
  pickerValue: any;
  setPickerValue: (v: any) => void;
  brushOpacity: number;
  setBrushOpacity: (v: number) => void;
  blurRadius: number;
  setBlurRadius: (v: number) => void;
  usePressure: boolean;
  setUsePressure: (v: boolean) => void;
  simPressure: boolean;
  setSimPressure: (v: boolean) => void;
  simPressureRandomness: number;
  setSimPressureRandomness: (v: number) => void;
  drawProjectionMode: 'wrap' | 'project' | 'inverse';
  setDrawProjectionMode: (m: 'wrap' | 'project' | 'inverse') => void;
  stampProjectionMode: 'wrap' | 'project' | 'inverse';
  setStampProjectionMode: (m: 'wrap' | 'project' | 'inverse') => void;
  stampSize: number;
  setStampSize: (v: number) => void;
  stampRotation: number;
  setStampRotation: (v: number) => void;
  smoothness: number;
  setSmoothness: (v: number) => void;
  fillFloodMode: 'island' | 'noncontiguous' | 'global';
  setFillFloodMode: (m: 'island' | 'noncontiguous' | 'global') => void;
  fillTolerance: number;
  setFillTolerance: (v: number) => void;
  texRes: TexRes;
  setTexRes: (r: TexRes) => void;
  dupMode: 'hero' | 'diptych' | 'triptych' | 'quadriptych';
  setDupMode: (m: 'hero' | 'diptych' | 'triptych' | 'quadriptych') => void;
  viewportBgMode: 'black' | 'white' | 'transparent';
  setViewportBgMode: (m: 'black' | 'white' | 'transparent') => void;
  uvBgMode: 'black' | 'white' | 'transparent';
  setUvBgMode: (m: 'black' | 'white' | 'transparent') => void;
  heroFeaturesEnabled: boolean;
  setHeroFeaturesEnabled: (v: boolean) => void;
  showGrid: boolean;
  setShowGrid: (v: boolean) => void;
  showManipulator: boolean;
  setShowManipulator: (v: boolean) => void;
  showFps: boolean;
  setShowFps: (v: boolean) => void;
  showHotkeyHud: boolean;
  setShowHotkeyHud: (v: boolean) => void;
  overlayArchitecture: boolean;
  setOverlayArchitecture: (v: boolean) => void;
  overlaySafety: boolean;
  setOverlaySafety: (v: boolean) => void;
  stampPickerOpen: boolean;
  setStampPickerOpen: (v: boolean) => void;
  projectModalOpen: boolean;
  setProjectModalOpen: (v: boolean) => void;
  renderModalOpen: boolean;
  setRenderModalOpen: (v: boolean) => void;
  hotkeysOpen: boolean;
  setHotkeysOpen: (v: boolean) => void;
  currentProjectName: string;
  setCurrentProjectName: (v: string) => void;
  textFont: string;
  setTextFont: (v: string) => void;
  textBold: boolean;
  setTextBold: (v: boolean) => void;
  textItalic: boolean;
  setTextItalic: (v: boolean) => void;
  textFontSize: number;
  setTextFontSize: (v: number) => void;
  textContent: string;
  setTextContent: (v: string) => void;
  textJustify: 'left' | 'center' | 'right';
  setTextJustify: (v: 'left' | 'center' | 'right') => void;
  textLetterSpacing: number;
  setTextLetterSpacing: (v: number) => void;
  textLineHeight: number;
  setTextLineHeight: (v: number) => void;
  // ── Camera track ────────────────────────────────────────────────────────────
  cameraTrackEnabled: boolean;
  setCameraTrackEnabled: (v: boolean) => void;
  cameraFov: number;
  setCameraFov: (v: number) => void;
  cameraKeyframes: CameraKeyframe;
  setCameraKeyframe: (frame: number, value: { x: number; y: number; z: number; fov: number }) => void;
  removeCameraKeyframe: (frame: number) => void;
  /** Live camera position, synced by Viewport3D so other components can read it. */
  currentCamPos: { x: number; y: number; z: number };
  setCurrentCamPos: (pos: { x: number; y: number; z: number }) => void;
  // (kf undo/redo is handled by the unified stack in Viewport3D)
  // ── Custom color swatches ──────────────────────────────────────────────────
  /** Up to 20 recently-used hex colors, most-recent first. */
  customColors: string[];
  addCustomColor: (hex: string) => void;
  // ── Active-drawing flag (set by Viewport3D) ──────────────────────────────────
  /** True while the user is actively painting a stroke. Used to disable pointer
   *  events on overlay panels so DOM hit-testing doesn't compete with drawing. */
  isDrawingActive: boolean;
  setIsDrawingActive: (v: boolean) => void;
  // ── Tablet mode ──────────────────────────────────────────────────────────────
  tabletMode: boolean;
  setTabletMode: (v: boolean) => void;
  rightPanelOpen: boolean;
  setRightPanelOpen: (v: boolean) => void;
  timelineOpen: boolean;
  setTimelineOpen: (v: boolean) => void;
  tabletCameraMode: 'orbit' | 'zoom' | 'pan';
  setTabletCameraMode: (m: 'orbit' | 'zoom' | 'pan') => void;
  tabletSphereMode: 'draw' | 'rotateLayer' | 'translateLayer';
  setTabletSphereMode: (m: 'draw' | 'rotateLayer' | 'translateLayer') => void;
  // ── Autosave status (drives footer dot) ──────────────────────────────────────
  autosaveStatus: 'clean' | 'dirty' | 'saving';
  setAutosaveStatus: (s: 'clean' | 'dirty' | 'saving') => void;
  // ── Viewport swap ─────────────────────────────────────────────────────────────
  /** When true, UVPanel is the primary (main) viewport; drawing tools paint into it directly. */
  uvViewportMain: boolean;
  setUvViewportMain: (v: boolean) => void;
  // ── Version tracking ───────────────────────────────────────────────────────
  /** The app version string the user last saw the What's New modal for. */
  lastSeenVersion: string;
  setLastSeenVersion: (v: string) => void;
}

const DEFAULT_LAYER_ID = 'layer-1';

export const useAppStore = create<AppState>((set, get) => ({
  layers: [
    {
      id: DEFAULT_LAYER_ID,
      name: 'Layer 1',
      visible: true,
      opacity: 1,
      translate: { x: 0, y: 0 },
      rotate: { x: 0, y: 0, z: 0 },
    },
  ],
  selectedLayerId: DEFAULT_LAYER_ID,
  selectLayer: (id) => set({ selectedLayerId: id, selectedLayerIds: id ? [id] : [] }),
  selectedLayerIds: [DEFAULT_LAYER_ID],
  addToSelection: (id) =>
    set((state) => {
      const next = new Set(state.selectedLayerIds);
      next.add(id);
      return { selectedLayerIds: Array.from(next) };
    }),
  removeFromSelection: (id) =>
    set((state) => {
      const next = new Set(state.selectedLayerIds);
      next.delete(id);
      return { selectedLayerIds: Array.from(next) };
    }),
  toggleSelection: (id) =>
    set((state) => {
      const next = new Set(state.selectedLayerIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedLayerIds: Array.from(next) };
    }),
  clearSelection: () => set({ selectedLayerIds: [], selectedLayerId: null }),
  selectLayers: (ids) => set({ selectedLayerIds: ids, selectedLayerId: ids[0] ?? null }),
  selectLayerRange: (fromId, toId) =>
    set((state) => {
      const fromIdx = state.layers.findIndex((l) => l.id === fromId);
      const toIdx = state.layers.findIndex((l) => l.id === toId);
      if (fromIdx < 0 || toIdx < 0) return {};
      const minIdx = Math.min(fromIdx, toIdx);
      const maxIdx = Math.max(fromIdx, toIdx);
      const rangeIds = state.layers.slice(minIdx, maxIdx + 1).map((l) => l.id);
      return { selectedLayerIds: rangeIds, selectedLayerId: rangeIds[0] ?? null };
    }),

  addLayer: (layer) =>
    set((state) => ({
      layers: [layer, ...state.layers],
      layerKeyframes: { ...state.layerKeyframes, [layer.id]: emptyLayerKeyframes() },
    })),

  updateLayer: (id, patch) =>
    set((state) => ({
      layers: state.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    })),

  removeLayer: (id) =>
    set((state) => {
      const { [id]: _dropped, ...remainingKfs } = state.layerKeyframes;
      const newSelectedIds = state.selectedLayerIds.filter((lid) => lid !== id);
      return {
        layers: state.layers.filter((l) => l.id !== id),
        selectedLayerId: state.selectedLayerId === id ? newSelectedIds[0] ?? null : state.selectedLayerId,
        selectedLayerIds: newSelectedIds,
        layerKeyframes: remainingKfs,
      };
    }),

  moveLayer: (fromIndex, toIndex) =>
    set((state) => {
      const layers = [...state.layers];
      const [moved] = layers.splice(fromIndex, 1);
      layers.splice(toIndex, 0, moved);
      return { layers };
    }),

  setLayersOrder: (layers) => set({ layers }),

  pendingMergeDown: null,
  requestMergeLayerDown: (topId, bottomId) => set({ pendingMergeDown: { topId, bottomId } }),
  clearMergeRequest: () => set({ pendingMergeDown: null }),

  textureCanvas: null,
  setTextureCanvas: (c) => set({ textureCanvas: c }),
  textureVersion: 0,
  bumpTextureVersion: () =>
    set((state) => ({ textureVersion: state.textureVersion + 1 })),

  layerThumbnails: {},
  setLayerThumbnail: (id, dataUrl) =>
    set((state) => ({ layerThumbnails: { ...state.layerThumbnails, [id]: dataUrl } })),

  uvFullCanvas: null,
  setUvFullCanvas: (c) => set({ uvFullCanvas: c }),
  uvCanvasVersion: 0,
  bumpUvCanvas: () => set((state) => ({ uvCanvasVersion: state.uvCanvasVersion + 1 })),

  stampImage: null,
  setStampImage: (c) => set({ stampImage: c }),
  stampIsPreset: false,
  setStampIsPreset: (v) => set({ stampIsPreset: v }),
  stampFlipX: false,
  setStampFlipX: (v) => set({ stampFlipX: v }),
  stampFlipY: false,
  setStampFlipY: (v) => set({ stampFlipY: v }),
  inverseWarpK: 1.5,
  setInverseWarpK: (v) => set({ inverseWarpK: v }),
  inverseWarpStrength: 0.85,
  setInverseWarpStrength: (v) => set({ inverseWarpStrength: v }),

  clipboardPasteCanvas: null,
  clipboardPasteScale: 1.0,
  clipboardPasteFitScale: 1.0,
  setClipboardPasteCanvas: (c) => set({ clipboardPasteCanvas: c }),
  setClipboardPasteScale: (s) => set({ clipboardPasteScale: s }),
  setClipboardPasteFitScale: (s) => set({ clipboardPasteFitScale: s }),

  // timeline
  fps: 15,
  totalFrames: 900, // 60 s × 15 fps
  currentFrame: 0,
  isPlaying: false,
  setFps: (fps) => set({ fps }),
  setTotalFrames: (n) => set({ totalFrames: n }),
  setCurrentFrame: (n) => { set({ currentFrame: n }); runKeyframeInterpolation(); },
  setIsPlaying: (v) => set({ isPlaying: v }),
  inPoint: null,
  outPoint: null,
  setInPoint: (n) => set({ inPoint: n }),
  setOutPoint: (n) => set({ outPoint: n }),
  playbackMode: 'once',
  setPlaybackMode: (m) => set({ playbackMode: m }),

  // auto-key
  autoKey: (_savedPrefs.autoKey ?? 0) as 0 | 1 | 2,
  setAutoKey: (v) => set({ autoKey: v }),

  // render settings
  renderSettings: { ...DEFAULT_RENDER_SETTINGS },
  setRenderSettings: (s) => set((state) => ({ renderSettings: { ...state.renderSettings, ...s } })),

  // camera track
  cameraTrackEnabled: false,
  setCameraTrackEnabled: (v) => set({ cameraTrackEnabled: v }),
  cameraFov: _savedPrefs.cameraFov ?? 30,
  setCameraFov: (v) => set({ cameraFov: v }),
  cameraKeyframes: {} as CameraKeyframe,
  setCameraKeyframe: (frame, value) =>
    set((state) => ({ cameraKeyframes: { ...state.cameraKeyframes, [frame]: value } })),
  removeCameraKeyframe: (frame) =>
    set((state) => {
      const next = { ...state.cameraKeyframes };
      delete next[frame];
      return { cameraKeyframes: next };
    }),
  currentCamPos: { x: 0, y: 0, z: 3 },
  setCurrentCamPos: (pos) => set({ currentCamPos: pos }),

  // keyframes — initialise default layer
  layerKeyframes: { [DEFAULT_LAYER_ID]: emptyLayerKeyframes() },
  keyframeEasings: {},

  setKeyframe: (layerId, prop, frame, value) =>
    set((state) => {
      const layer = state.layers.find((l) => l.id === layerId);
      if (layer?.locked) return {};
      const existing = state.layerKeyframes[layerId] ?? emptyLayerKeyframes();
      return {
        layerKeyframes: {
          ...state.layerKeyframes,
          [layerId]: { ...existing, [prop]: { ...existing[prop], [frame]: value } },
        },
      };
    }),

  removeKeyframe: (layerId, prop, frame) =>
    set((state) => {
      const existing = state.layerKeyframes[layerId];
      if (!existing) return {};
      const propMap = { ...existing[prop] } as Record<number, any>;
      delete propMap[frame];
      return {
        layerKeyframes: {
          ...state.layerKeyframes,
          [layerId]: { ...existing, [prop]: propMap },
        },
      };
    }),

  setKeyframeEasing: (layerId, prop, frame, data) =>
    set((state) => {
      const layerEasings = state.keyframeEasings[layerId] ?? {};
      const propEasings  = layerEasings[prop] ?? {};
      const existing     = propEasings[frame] ?? { in: 'linear', out: 'linear' };
      return {
        keyframeEasings: {
          ...state.keyframeEasings,
          [layerId]: { ...layerEasings, [prop]: { ...propEasings, [frame]: { ...existing, ...data } } },
        },
      };
    }),

  resetToDefaults: () =>
    set({
      layers: [{ id: DEFAULT_LAYER_ID, name: 'Layer 1', visible: true, opacity: 1,
        translate: { x: 0, y: 0 }, rotate: { x: 0, y: 0, z: 0 } }],
      selectedLayerId: DEFAULT_LAYER_ID,
      selectedLayerIds: [DEFAULT_LAYER_ID],
      layerKeyframes: { [DEFAULT_LAYER_ID]: emptyLayerKeyframes() },
      cameraTrackEnabled: false,
      cameraKeyframes: {},
      layerThumbnails: {},
      fps: 15,
      totalFrames: 900,
      currentFrame: 0,
      isPlaying: false,
      inPoint: null,
      outPoint: null,
      renderSettings: { ...DEFAULT_RENDER_SETTINGS },
    }),

  restoreProject: (data) =>
    set((state) => ({
      layers: data.layers,
      selectedLayerId: data.selectedLayerId,
      selectedLayerIds: data.selectedLayerId ? [data.selectedLayerId] : [],
      layerKeyframes: data.layerKeyframes,
      layerThumbnails: {},
      fps: data.fps,
      totalFrames: data.totalFrames,
      currentFrame: 0,
      isPlaying: false,
      inPoint: null,
      outPoint: null,
      renderSettings: data.renderSettings ?? state.renderSettings,
    })),

  restoreLayerState: (layers, selectedLayerId, layerKeyframes, cameraKeyframes?) =>
    cameraKeyframes
      ? set({ layers, selectedLayerId, layerKeyframes, cameraKeyframes })
      : set({ layers, selectedLayerId, layerKeyframes }),

  // ── Toolbar / painting settings ─────────────────────────────────────────────
  toolMode: (_savedPrefs.toolMode ?? 'draw') as 'draw' | 'fill' | 'erase' | 'stamp' | 'text' | 'blur',
  setToolMode: (m) => set({ toolMode: m }),
  brushSize: _savedPrefs.brushSize ?? 0.075,
  setBrushSize: (v) => set({ brushSize: v }),
  brushColor: _savedPrefs.brushColor ?? '#108ee9',
  setBrushColor: (v) => set({ brushColor: v }),
  pickerValue: (_savedPrefs.brushColor ?? '#108ee9') as any,
  setPickerValue: (v) => set({ pickerValue: v }),
  brushOpacity: _savedPrefs.brushOpacity ?? 1,
  setBrushOpacity: (v) => set({ brushOpacity: v }),
  blurRadius: _savedPrefs.blurRadius ?? 0.3,
  setBlurRadius: (v) => set({ blurRadius: v }),
  usePressure: _savedPrefs.usePressure ?? false,
  setUsePressure: (v) => set({ usePressure: v }),
  simPressure: _savedPrefs.simPressure ?? false,
  setSimPressure: (v) => set({ simPressure: v }),
  simPressureRandomness: _savedPrefs.simPressureRandomness ?? 0.2,
  setSimPressureRandomness: (v) => set({ simPressureRandomness: v }),
  drawProjectionMode: (_savedPrefs.drawProjectionMode ?? 'wrap') as 'wrap' | 'project' | 'inverse',
  setDrawProjectionMode: (m) => set({ drawProjectionMode: m }),
  stampProjectionMode: (_savedPrefs.stampProjectionMode ?? 'wrap') as 'wrap' | 'project' | 'inverse',
  setStampProjectionMode: (m) => set({ stampProjectionMode: m }),
  stampSize: 0.08,
  setStampSize: (v) => set({ stampSize: v }),
  stampRotation: 0,
  setStampRotation: (v) => set({ stampRotation: v }),
  smoothness: _savedPrefs.smoothness ?? 0.5,
  setSmoothness: (v) => set({ smoothness: v }),
  fillFloodMode: ((): 'island' | 'noncontiguous' | 'global' => {
    const saved = _savedPrefs.fillFloodMode;
    if (saved === 'noncontiguous') return 'noncontiguous';
    if (saved === 'global') return 'global';
    return 'island'; // default; also maps old 'contiguous' value
  })(),
  setFillFloodMode: (m) => set({ fillFloodMode: m }),
  fillTolerance: _savedPrefs.fillTolerance ?? 15,
  setFillTolerance: (v) => set({ fillTolerance: v }),
  texRes: TEX_RESOLUTIONS.find((r) => r.label === _savedPrefs.texResLabel) ?? TEX_RESOLUTIONS[2],
  setTexRes: (r) => set({ texRes: r }),
  dupMode: (_savedPrefs.dupMode ?? 'diptych') as 'hero' | 'diptych' | 'triptych' | 'quadriptych',
  setDupMode: (m) => set({ dupMode: m }),
  viewportBgMode: (_savedPrefs.viewportBgMode ?? 'white') as 'black' | 'white' | 'transparent',
  setViewportBgMode: (m) => set({ viewportBgMode: m }),
  uvBgMode: (_savedPrefs.uvBgMode ?? 'white') as 'black' | 'white' | 'transparent',
  setUvBgMode: (m) => set({ uvBgMode: m }),
  heroFeaturesEnabled: _savedPrefs.heroFeaturesEnabled ?? true,
  setHeroFeaturesEnabled: (v) => set({ heroFeaturesEnabled: v }),
  showGrid: _savedPrefs.showGrid ?? true,
  setShowGrid: (v) => set({ showGrid: v }),
  showManipulator: _savedPrefs.showManipulator ?? false,
  setShowManipulator: (v) => set({ showManipulator: v }),
  showFps: _savedPrefs.showFps ?? false,
  setShowFps: (v) => set({ showFps: v }),
  showHotkeyHud: _savedPrefs.showHotkeyHud ?? true,
  setShowHotkeyHud: (v) => set({ showHotkeyHud: v }),
  overlayArchitecture: _savedPrefs.overlayArchitecture ?? true,
  setOverlayArchitecture: (v) => set({ overlayArchitecture: v }),
  overlaySafety: _savedPrefs.overlaySafety ?? false,
  setOverlaySafety: (v) => set({ overlaySafety: v }),
  stampPickerOpen: false,
  setStampPickerOpen: (v) => set({ stampPickerOpen: v }),
  projectModalOpen: false,
  setProjectModalOpen: (v) => set({ projectModalOpen: v }),
  renderModalOpen: false,
  setRenderModalOpen: (v) => set({ renderModalOpen: v }),
  hotkeysOpen: false,
  setHotkeysOpen: (v) => set({ hotkeysOpen: v }),
  currentProjectName: 'Untitled',
  setCurrentProjectName: (v) => set({ currentProjectName: v }),
  textFont: 'Arial',
  setTextFont: (v) => set({ textFont: v }),
  textBold: false,
  setTextBold: (v) => set({ textBold: v }),
  textItalic: false,
  setTextItalic: (v) => set({ textItalic: v }),
  textFontSize: 256,
  setTextFontSize: (v) => set({ textFontSize: v }),
  textContent: 'Sphere',
  setTextContent: (v) => set({ textContent: v }),
  textJustify: 'center' as 'left' | 'center' | 'right',
  setTextJustify: (v) => set({ textJustify: v }),
  textLetterSpacing: 0,
  setTextLetterSpacing: (v) => set({ textLetterSpacing: v }),
  textLineHeight: 1.4,
  setTextLineHeight: (v) => set({ textLineHeight: v }),
  // ── Custom color swatches ──────────────────────────────────────────────────
  customColors: (_savedPrefs.customColors ?? []) as string[],
  addCustomColor: (hex) => {
    if (!hex || hex.startsWith('linear-gradient') || hex.startsWith('radial-gradient')) return;
    set((state) => {
      const deduped = [hex, ...state.customColors.filter((c) => c !== hex)].slice(0, 8);
      return { customColors: deduped };
    });
  },
  // ── Active-drawing flag ─────────────────────────────────────────────────────
  isDrawingActive: false,
  setIsDrawingActive: (v) => set({ isDrawingActive: v }),
  // ── Tablet mode ──────────────────────────────────────────────────────────────
  tabletMode: (() => {
    if (_savedPrefs.tabletMode !== undefined) return _savedPrefs.tabletMode;
    return typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0
      && !window.matchMedia('(pointer: fine)').matches;
  })(),
  setTabletMode: (v) => {
    const isTablet = v;
    set((state) => ({
      tabletMode: isTablet,
      // Auto-close panels on tablet; auto-open on desktop.
      rightPanelOpen: isTablet ? false : true,
      timelineOpen: isTablet ? false : true,
    }));
  },
  rightPanelOpen: !(_savedPrefs.tabletMode ?? (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 && !window.matchMedia('(pointer: fine)').matches)),
  setRightPanelOpen: (v) => set({ rightPanelOpen: v }),
  timelineOpen: !(_savedPrefs.tabletMode ?? (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 && !window.matchMedia('(pointer: fine)').matches)),
  setTimelineOpen: (v) => set({ timelineOpen: v }),
  tabletCameraMode: 'orbit',
  setTabletCameraMode: (m) => set({ tabletCameraMode: m }),
  tabletSphereMode: 'draw',
  setTabletSphereMode: (m) => set({ tabletSphereMode: m }),
  // ── Autosave status ────────────────────────────────────────────────────────
  autosaveStatus: 'clean',
  setAutosaveStatus: (s) => set({ autosaveStatus: s }),
  // ── Viewport swap ──────────────────────────────────────────────────────────
  uvViewportMain: false,
  setUvViewportMain: (v) => set({ uvViewportMain: v }),
  // ── Version tracking ──────────────────────────────────────────────────────
  lastSeenVersion: _savedPrefs.lastSeenVersion ?? '',
  setLastSeenVersion: (v) => set({ lastSeenVersion: v }),
}));

// ── Auto-save preferences ──────────────────────────────────────────────────
let _prefsSaveTimer: ReturnType<typeof setTimeout> | null = null;

// ── Keyframe interpolation driver ─────────────────────────────────────────
// Module-level rounded-frame tracker so the guard works across all callers.
let _lastInterpRf = -1;
/**
 * Applies keyframe interpolation for the current frame synchronously.
 * Guarded on rounded-frame equality so it is safe to call every RAF tick;
 * it is a no-op when the integer frame has not changed.
 * Call this immediately after any state update that changes currentFrame so
 * the interpolated layer values are batched into the same React render.
 */
export function runKeyframeInterpolation(): void {
  const s = useAppStore.getState();
  const rf = Math.round(s.currentFrame);
  if (rf === _lastInterpRf) return;
  _lastInterpRf = rf;
  const next = interpolateLayers(s.layers, s.currentFrame, s.layerKeyframes, s.keyframeEasings);
  if (next.length === s.layers.length && next.every((l, i) => l === s.layers[i])) return;
  useAppStore.setState({ layers: next });
}

useAppStore.subscribe((state) => {
  if (_prefsSaveTimer) clearTimeout(_prefsSaveTimer);
  _prefsSaveTimer = setTimeout(() => {
    const prefs: SavedPrefs = {
      viewportBgMode: state.viewportBgMode,
      uvBgMode: state.uvBgMode,
      showGrid: state.showGrid,
      showManipulator: state.showManipulator,
      heroFeaturesEnabled: state.heroFeaturesEnabled,
      dupMode: state.dupMode,
      toolMode: state.toolMode,
      brushSize: state.brushSize,
      brushOpacity: state.brushOpacity,
      blurRadius: state.blurRadius,
      brushColor: state.brushColor,
      usePressure: state.usePressure,
      simPressure: state.simPressure,
      simPressureRandomness: state.simPressureRandomness,
      drawProjectionMode: state.drawProjectionMode,
      stampProjectionMode: state.stampProjectionMode,
      smoothness: state.smoothness,
      texResLabel: state.texRes.label,
      autoKey: state.autoKey,
      customColors: state.customColors,
      showFps: state.showFps,
      showHotkeyHud: state.showHotkeyHud,
      fillFloodMode: state.fillFloodMode,
      fillTolerance: state.fillTolerance,
      overlayArchitecture: state.overlayArchitecture,
      overlaySafety: state.overlaySafety,
      tabletMode: state.tabletMode,
      cameraFov: state.cameraFov,
      lastSeenVersion: state.lastSeenVersion,
    };
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
  }, 300);
});

